import { useMemo, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import { useDashboardTrends } from '../../hooks/useDashboard';
import { Tabs, TabsList, TabsTrigger } from '../ui/Tabs';

type Granularity = 'daily' | 'weekly' | 'monthly';

interface Props {
  filters: {
    month?: string;
    type?: 'HYGIENE' | 'COMPLIANCE';
    facilityTypeId?: string;
    geoLocationId?: string;
  };
  /** Limit how many charts to render (default: all 6) */
  limit?: number;
}

interface ChartDef {
  title: string;
  subtitle?: string;
  primaryKey: string;
  secondaryKey?: string;
  /** Override key used for the card-level aggregate stat (falls back to primaryKey) */
  aggregateKey?: string;
  /** Short label shown in the tooltip (defaults to title) */
  tooltipLabel?: string;
  /** Tooltip labels for dual-line charts */
  primaryLabel?: string;
  secondaryLabel?: string;
  chartType: 'area' | 'bar';
  primaryColor: string;
  secondaryColor?: string;
  isPercent: boolean;
  aggregate: 'sum' | 'avg';
}

const CHARTS: ChartDef[] = [
  {
    title: 'Average hygiene score',
    primaryKey: 'averageHygieneScore',
    secondaryKey: 'averageComplianceScore',
    primaryLabel: 'Hygiene',
    secondaryLabel: 'Compliance',
    chartType: 'area',
    primaryColor: 'var(--nhip-green)',
    secondaryColor: '#3B82F6',
    isPercent: true,
    aggregate: 'avg',
  },
  {
    title: 'Assessments conducted',
    primaryKey: 'hygieneAssessments',
    secondaryKey: 'complianceAssessments',
    aggregateKey: 'totalAssessments',
    chartType: 'bar',
    primaryColor: 'var(--nhip-green)',
    secondaryColor: '#3B82F6',
    isPercent: false,
    aggregate: 'sum',
  },
  {
    title: 'Facility registrations',
    primaryKey: 'facilityRegistrations',
    chartType: 'bar',
    primaryColor: 'var(--nhip-green)',
    isPercent: false,
    aggregate: 'sum',
  },
  {
    title: 'Coverage rate',
    subtitle: '% of facilities assessed per period',
    tooltipLabel: 'Coverage',
    primaryKey: 'coverageRate',
    chartType: 'area',
    primaryColor: '#F59E0B',
    isPercent: true,
    aggregate: 'avg',
  },
  {
    title: 'Follow-up completion',
    subtitle: '% of due follow-ups carried per period',
    tooltipLabel: 'Follow-up',
    primaryKey: 'followupCompletionRate',
    chartType: 'area',
    primaryColor: '#3B82F6',
    isPercent: true,
    aggregate: 'avg',
  },
  {
    title: 'Reassessment improvement',
    subtitle: '% of reassessments where score improved',
    tooltipLabel: 'Improvement',
    primaryKey: 'reassessmentImprovementRate',
    chartType: 'area',
    primaryColor: 'var(--nhip-green)',
    isPercent: true,
    aggregate: 'avg',
  },
];

const GRANULARITY_OPTIONS: { value: Granularity; label: string }[] = [
  { value: 'daily',   label: 'Daily'   },
  { value: 'weekly',  label: 'Weekly'  },
  { value: 'monthly', label: 'Monthly' },
];

function buildDateRange(granularity: Granularity, month?: string): { from: string; to: string } {
  const now = new Date();
  if (granularity === 'daily') {
    // Last 30 days — narrows to the selected month when one is active
    if (month) {
      const [y, m] = month.split('-').map(Number);
      return {
        from: new Date(y, m - 1, 1).toISOString().split('T')[0],
        to:   new Date(y, m, 0).toISOString().split('T')[0],
      };
    }
    const from = new Date(now); from.setDate(now.getDate() - 29);
    return { from: from.toISOString().split('T')[0], to: now.toISOString().split('T')[0] };
  }
  if (granularity === 'weekly') {
    // Always last 12 weeks regardless of month filter
    const from = new Date(now); from.setDate(now.getDate() - 83);
    return { from: from.toISOString().split('T')[0], to: now.toISOString().split('T')[0] };
  }
  // monthly — always last 12 months
  const from = new Date(now.getFullYear() - 1, now.getMonth(), 1);
  return { from: from.toISOString().split('T')[0], to: now.toISOString().split('T')[0] };
}

function periodLabel(granularity: Granularity, month?: string): string {
  if (granularity === 'daily') return 'Last 30 days · daily';
  if (month) {
    const [y, m] = month.split('-').map(Number);
    const label = new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    return `${label} · ${granularity}`;
  }
  if (granularity === 'weekly') return 'Last 12 weeks · weekly';
  return 'Last 12 months · monthly';
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function computeAvg(data: Record<string, any>[], key: string): number {
  if (!data.length) return 0;
  const vals = data.map(d => (d[key] as number) ?? 0);
  return vals.reduce((a, b) => a + b, 0) / vals.length;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function computeSum(data: Record<string, any>[], key: string): number {
  if (!data.length) return 0;
  return data.map(d => (d[key] as number) ?? 0).reduce((a, b) => a + b, 0);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function computeDelta(data: Record<string, any>[], def: ChartDef): number | null {
  if (data.length < 4) return null;
  const half  = Math.floor(data.length / 2);
  const first = data.slice(0, half);
  const second = data.slice(half);
  if (def.aggregate === 'sum') {
    const prev = computeSum(first, def.primaryKey);
    const curr = computeSum(second, def.primaryKey);
    if (prev === 0) return null;
    return ((curr - prev) / prev) * 100; // percentage change
  }
  // avg → absolute pp difference
  return computeAvg(second, def.primaryKey) - computeAvg(first, def.primaryKey);
}

function formatAggregate(val: number, def: ChartDef): string {
  if (def.isPercent) return `${val.toFixed(1)}%`;
  if (val >= 1000)   return val.toLocaleString();
  return Math.round(val).toLocaleString();
}

function formatDelta(delta: number, def: ChartDef): string {
  const abs = Math.abs(delta);
  if (def.aggregate === 'sum') return `${abs.toFixed(1)}%`;
  return `${abs.toFixed(1)}pp`;
}

function formatXLabel(label: string, granularity: Granularity): string {
  if (granularity === 'weekly') {
    const m = label.match(/^\d{4}-W(\d{1,2})$/);
    return m ? `W${m[1]}` : label;
  }
  if (granularity === 'monthly') {
    const [y, mo] = label.split('-');
    return new Date(Number(y), Number(mo) - 1, 1).toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  }
  return new Date(label).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatTooltipLabel(label: string, granularity: Granularity): string {
  if (granularity === 'weekly') {
    const m = label.match(/^(\d{4})-W(\d{1,2})$/);
    return m ? `Week ${m[2]}, ${m[1]}` : label;
  }
  if (granularity === 'monthly') {
    const [y, mo] = label.split('-');
    return new Date(Number(y), Number(mo) - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }
  return new Date(label).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

// Returns an axisLabel.interval-compatible value so visible labels are evenly spaced
function xInterval(granularity: Granularity, dataLength: number): number {
  if (granularity === 'monthly') return 0;      // always show all 12 months
  if (granularity === 'weekly')  return 0;      // always show all 12 weeks
  // daily: target ~6 visible labels
  if (dataLength <= 8)  return 0;
  return Math.ceil(dataLength / 6) - 1;
}

// ─── ISO week helpers ─────────────────────────────────────────────────────────

function getISOWeekNumber(date: Date): number {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
}

function getISOWeekYear(date: Date): number {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  return d.getUTCFullYear();
}

// ─── Pad chart data with zero-value rows for missing periods ──────────────────
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function padChartData(
  data: Record<string, any>[],
  granularity: Granularity,
  dateRange: { from: string; to: string }
// eslint-disable-next-line @typescript-eslint/no-explicit-any
): Record<string, any>[] {
  const labels: string[] = [];
  const from = new Date(dateRange.from + 'T00:00:00');
  const to   = new Date(dateRange.to   + 'T00:00:00');

  if (granularity === 'daily') {
    const cur = new Date(from);
    while (cur <= to) {
      labels.push(cur.toISOString().split('T')[0]);
      cur.setDate(cur.getDate() + 1);
    }
  } else if (granularity === 'weekly') {
    const cur = new Date(from);
    // Snap to Monday of the containing ISO week
    const day = cur.getDay() || 7;
    if (day !== 1) cur.setDate(cur.getDate() - (day - 1));
    while (cur <= to) {
      const wk = getISOWeekNumber(cur);
      const wy = getISOWeekYear(cur);
      labels.push(`${wy}-W${String(wk).padStart(2, '0')}`);
      cur.setDate(cur.getDate() + 7);
    }
  } else {
    // monthly
    let y = from.getFullYear();
    let m = from.getMonth() + 1;
    const toY = to.getFullYear();
    const toM = to.getMonth() + 1;
    while (y < toY || (y === toY && m <= toM)) {
      labels.push(`${y}-${String(m).padStart(2, '0')}`);
      m++;
      if (m > 12) { m = 1; y++; }
    }
  }

  const byLabel = new Map(data.map(d => [String(d.label), d]));
  return labels.map(label => byLabel.get(label) ?? { label });
}

// ─── Color helpers ─────────────────────────────────────────────────────────────

/** Resolves a `var(--token)` reference against the live DOM, leaves plain colors untouched. */
function resolveCssColor(value: string): string {
  if (!value.startsWith('var(')) return value;
  if (typeof document === 'undefined') return value;
  const varName = value.slice(4, -1).trim();
  const resolved = getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
  return resolved || value;
}

function withAlpha(hexOrCss: string, alpha: number): string {
  // hsl(...) / rgb(...) / hex — append an alpha channel the way each syntax expects
  if (hexOrCss.startsWith('#')) {
    const a = Math.round(alpha * 255).toString(16).padStart(2, '0');
    return `${hexOrCss}${a}`;
  }
  if (hexOrCss.startsWith('hsl(')) {
    return hexOrCss.replace('hsl(', 'hsla(').replace(')', ` / ${alpha})`);
  }
  if (hexOrCss.startsWith('rgb(')) {
    return hexOrCss.replace('rgb(', 'rgba(').replace(')', `, ${alpha})`);
  }
  return hexOrCss;
}

// ─── Tooltip builder — replicates the dark card tooltip used across the dashboard ──

function buildTooltipFormatter(def: ChartDef, granularity: Granularity, primaryColor: string, secondaryColor?: string) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (params: any) => {
    const list = Array.isArray(params) ? params : [params];
    if (!list.length) return '';
    const axisLabel = list[0].axisValueLabel ?? list[0].axisValue ?? list[0].name;
    const dateLabel = formatTooltipLabel(String(axisLabel), granularity);
    const fmt = (v: number) => (def.isPercent ? `${v.toFixed(1)}%` : v.toLocaleString());

    const rows = list
      .map((p: any) => {
        const isSecondary = p.seriesName === (def.secondaryLabel ?? '__secondary__');
        const color = isSecondary ? secondaryColor : primaryColor;
        const label = isSecondary ? def.secondaryLabel : (def.primaryLabel ?? def.tooltipLabel ?? def.title);
        const val = Array.isArray(p.value) ? p.value[1] : p.value;
        if (val == null) return '';
        return `<div style="display:flex;align-items:center;gap:6px;margin-top:4px;">
          <span style="width:10px;height:10px;border-radius:2px;background:${color};flex-shrink:0;"></span>
          <span style="font-size:12px;color:#fff;">${label}: <strong>${fmt(val)}</strong></span>
        </div>`;
      })
      .join('');

    return `<div style="min-width:150px;">
      <div style="font-size:13px;font-weight:700;color:#fff;margin-bottom:2px;">${dateLabel}</div>
      ${rows}
    </div>`;
  };
}

// ─── Skeleton ────────────────────────────────────────────────────────────────

function SkeletonCard() {
  return (
    <div
      className='bg-white rounded-lg p-4 flex flex-col gap-2 animate-pulse'
      style={{ border: '1px solid var(--nhip-border)' }}
    >
      <div className='flex justify-between'>
        <div className='h-4 w-36 rounded' style={{ backgroundColor: 'var(--nhip-border)' }} />
        <div className='h-7 w-16 rounded' style={{ backgroundColor: 'var(--nhip-border)' }} />
      </div>
      <div className='flex justify-between'>
        <div className='h-3 w-28 rounded' style={{ backgroundColor: 'var(--nhip-border)' }} />
        <div className='h-3 w-24 rounded' style={{ backgroundColor: 'var(--nhip-border)' }} />
      </div>
      <div className='h-[220px] w-full rounded mt-2' style={{ backgroundColor: 'var(--nhip-border)' }} />
    </div>
  );
}

// ─── MiniChart ───────────────────────────────────────────────────────────────

interface MiniChartProps {
  def: ChartDef;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: Record<string, any>[];
  granularity: Granularity;
  month?: string;
  dateRange: { from: string; to: string };
}

function MiniChart({ def, data, granularity, month, dateRange }: MiniChartProps) {
  const aggregate = useMemo(() => {
    if (!data.length) return 0;
    const key = def.aggregateKey ?? def.primaryKey;
    return def.aggregate === 'sum'
      ? computeSum(data, key)
      : computeAvg(data, key);
  }, [data, def]);

  const delta = useMemo(() => computeDelta(data, def), [data, def]);

  // Pad chart data so all periods in the date range are present (missing → zero row)
  const chartData = useMemo(() => padChartData(data, granularity, dateRange), [data, granularity, dateRange]);

  const period = periodLabel(granularity, month);
  const isPositive = (delta ?? 0) > 0;
  const deltaColor = isPositive ? 'var(--nhip-green)' : 'var(--nhip-red)';
  const deltaArrow = isPositive ? '↑' : '↓';

  const option: EChartsOption = useMemo(() => {
    const primaryColor = resolveCssColor(def.primaryColor);
    const secondaryColor = def.secondaryColor ? resolveCssColor(def.secondaryColor) : undefined;
    const borderColor = resolveCssColor('var(--nhip-border)');
    const mutedColor = resolveCssColor('var(--nhip-ink-muted)');

    const categories = chartData.map(d => String(d.label));
    const axisLabelInterval = xInterval(granularity, chartData.length);

    const baseGrid = { left: 36, right: 12, top: 10, bottom: 24, containLabel: false };
    const baseXAxis = {
      type: 'category' as const,
      data: categories,
      boundaryGap: def.chartType === 'bar',
      axisLine: { lineStyle: { color: borderColor } },
      axisTick: { show: false },
      axisLabel: {
        fontSize: 9,
        color: mutedColor,
        interval: axisLabelInterval,
        formatter: (v: string) => formatXLabel(v, granularity),
      },
    };
    const baseYAxis = {
      type: 'value' as const,
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { lineStyle: { color: borderColor, type: 'dashed' as const } },
      axisLabel: {
        fontSize: 9,
        color: mutedColor,
        formatter: def.isPercent ? (v: number) => `${v}%` : undefined,
      },
      min: def.isPercent ? (v: { min: number }) => Math.max(0, Math.floor(v.min) - 5) : undefined,
      max: def.isPercent ? (v: { max: number }) => Math.min(100, Math.ceil(v.max) + 5) : undefined,
    };

    const tooltip = {
      trigger: 'axis' as const,
      backgroundColor: '#1F2937',
      borderWidth: 0,
      padding: [10, 12],
      formatter: buildTooltipFormatter(def, granularity, primaryColor, secondaryColor),
    };

    if (def.chartType === 'bar') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const series: any[] = [
        {
          name: def.primaryLabel ?? def.title,
          type: 'bar',
          data: chartData.map(d => d[def.primaryKey] ?? 0),
          stack: def.secondaryKey ? 'stack' : undefined,
          itemStyle: {
            color: withAlpha(primaryColor, def.secondaryKey ? 0.3 : 0.15),
            borderColor: primaryColor,
            borderWidth: 1,
            borderRadius: def.secondaryKey ? [0, 0, 0, 0] : [2, 2, 0, 0],
          },
          barMaxWidth: 24,
        },
      ];
      if (def.secondaryKey) {
        series.push({
          name: def.secondaryLabel ?? '__secondary__',
          type: 'bar',
          data: chartData.map(d => d[def.secondaryKey!] ?? 0),
          stack: 'stack',
          itemStyle: {
            color: withAlpha(secondaryColor!, 0.4),
            borderColor: secondaryColor,
            borderWidth: 1,
            borderRadius: [2, 2, 0, 0],
          },
          barMaxWidth: 24,
        });
      }
      return { grid: baseGrid, xAxis: baseXAxis, yAxis: baseYAxis, tooltip, series };
    }

    // Area (line) chart — supports 1 or 2 lines, secondary drawn first so primary sits on top
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const series: any[] = [];
    if (def.secondaryKey) {
      series.push({
        name: def.secondaryLabel ?? '__secondary__',
        type: 'line',
        data: chartData.map(d => d[def.secondaryKey!] ?? null),
        smooth: true,
        symbol: 'circle',
        symbolSize: 5,
        showSymbol: false,
        lineStyle: { color: secondaryColor, width: 1.5 },
        itemStyle: { color: secondaryColor },
        areaStyle: {
          color: {
            type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0, color: withAlpha(secondaryColor!, 0.1) },
              { offset: 1, color: withAlpha(secondaryColor!, 0) },
            ],
          },
        },
      });
    }
    series.push({
      name: def.primaryLabel ?? def.title,
      type: 'line',
      data: chartData.map(d => d[def.primaryKey] ?? null),
      smooth: true,
      symbol: 'circle',
      symbolSize: 5,
      showSymbol: false,
      lineStyle: { color: primaryColor, width: 2 },
      itemStyle: { color: primaryColor },
      areaStyle: {
        color: {
          type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
          colorStops: [
            { offset: 0, color: withAlpha(primaryColor, 0.18) },
            { offset: 1, color: withAlpha(primaryColor, 0) },
          ],
        },
      },
    });

    return { grid: baseGrid, xAxis: baseXAxis, yAxis: baseYAxis, tooltip, series };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartData, def, granularity]);

  return (
    <div
      className='bg-white rounded-lg p-4 flex flex-col gap-1'
      style={{ border: '1px solid var(--nhip-border)' }}
    >
      {/* Header */}
      <div className='flex items-start justify-between gap-2'>
        <p className='text-sm font-bold leading-tight' style={{ color: 'var(--nhip-ink)' }}>
          {def.title}
        </p>
        {data.length > 0 && (
          <p className='text-xl font-bold flex-shrink-0 tracking-tight' style={{ color: 'var(--nhip-ink)' }}>
            {formatAggregate(aggregate, def)}
          </p>
        )}
      </div>

      {/* Sub-header */}
      <div className='flex items-center justify-between gap-2'>
        <p className='text-xs leading-none' style={{ color: 'var(--nhip-ink-muted)' }}>
          {def.subtitle ?? period}
        </p>
        {delta !== null && (
          <div className='flex items-center gap-1 flex-shrink-0'>
            <span className='text-xs font-semibold' style={{ color: deltaColor }}>
              {deltaArrow} {formatDelta(delta, def)}
            </span>
            <span className='text-xs' style={{ color: 'var(--nhip-ink-muted)' }}>vs prior</span>
          </div>
        )}
      </div>

      {/* Chart */}
      <div className='mt-3'>
        {data.length === 0 ? (
          <div className='h-[220px] flex items-center justify-center'>
            <span className='text-xs' style={{ color: 'var(--nhip-ink-muted)' }}>No data</span>
          </div>
        ) : (
          <ReactECharts option={option} style={{ height: 220, width: '100%' }} notMerge opts={{ renderer: 'svg' }} />
        )}
      </div>
    </div>
  );
}

// ─── ActivityTrends ───────────────────────────────────────────────────────────

export default function ActivityTrends({ filters, limit }: Props) {
  const [granularity, setGranularity] = useState<Granularity>('monthly');

  const dateRange = buildDateRange(granularity, filters.month);

  const { data, isLoading } = useDashboardTrends({
    granularity,
    from: dateRange.from,
    to: dateRange.to,
    type: filters.type,
    facilityTypeId: filters.facilityTypeId,
    geoLocationId: filters.geoLocationId,
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const chartData: Record<string, any>[] = (data as any)?.buckets ?? [];

  return (
    <div className='space-y-3'>
      {/* Section header */}
      <div className='flex items-start justify-between gap-4'>
        <div>
          <h2 className='text-base font-bold' style={{ color: 'var(--nhip-ink)' }}>Activity trends</h2>
          <p className='text-xs mt-0.5' style={{ color: 'var(--nhip-ink-muted)' }}>
            Scores, assessments &amp; registrations over time
          </p>
        </div>
        <Tabs value={granularity} onValueChange={v => setGranularity(v as Granularity)} className='flex-shrink-0'>
          <TabsList>
            {GRANULARITY_OPTIONS.map(opt => (
              <TabsTrigger key={opt.value} value={opt.value} className='px-3 py-1 text-xs'>
                {opt.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      {/* 2×3 grid */}
      <div className='space-y-3'>
        {/* Row 1 — score chart wider (1.3fr 1fr 1fr on lg) */}
        <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[1.3fr_1fr_1fr] gap-3'>
          {isLoading
            ? Array.from({ length: 3 }).map((_, i) => <SkeletonCard key={i} />)
            : CHARTS.slice(0, 3).map(def => (
                <MiniChart
                  key={def.primaryKey}
                  def={def}
                  data={chartData}
                  granularity={granularity}
                  month={filters.month}
                  dateRange={dateRange}
                />
              ))
          }
        </div>
        {/* Row 2 — equal columns (hidden when limit=3) */}
        {(limit == null || limit > 3) && (
          <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3'>
            {isLoading
              ? Array.from({ length: 3 }).map((_, i) => <SkeletonCard key={i + 3} />)
              : CHARTS.slice(3).map(def => (
                  <MiniChart
                    key={def.primaryKey}
                    def={def}
                    data={chartData}
                    granularity={granularity}
                    month={filters.month}
                    dateRange={dateRange}
                  />
                ))
            }
          </div>
        )}
      </div>
    </div>
  );
}
